import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { DELETE_STANDARD_LABEL, GET_STANDARD_LABELS } from '@system-config/label-management/graphql';
import { useState, useCallback } from 'react';

interface DeleteStandardLabelResponse {
  deleteStandardLabel: {
    success: boolean;
  };
}

export function useDeleteStandardLabel() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deleteStandardLabelMutation, { loading }] = useMutation<DeleteStandardLabelResponse>(DELETE_STANDARD_LABEL);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deleteStandardLabel = async (id: string): Promise<boolean> => {
    try {
      track('DeleteStandardLabelInitiated', {
        labelId: id,
      });

      await deleteStandardLabelMutation({
        variables: { id },
        refetchQueries: [{ query: GET_STANDARD_LABELS }],
        awaitRefetchQueries: true,
      });

      track('DeleteStandardLabelSucceeded', {
        labelId: id,
      });

      return true;
    } catch (error) {
      track('DeleteStandardLabelFailed', {
        labelId: id,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deleteStandardLabel,
    loading,
    errors,
    resetErrors,
  };
}

export default useDeleteStandardLabel;
